/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useCookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import { sendChat, getChatHistory, getBaiboonSettings, DEFAULT_BAIBOON_CHIPS, DEFAULT_BAIBOON_CHIPS_UNMATCHED } from 'src/sections/baiboonAdmin/api';
import BaiboonAnswerView from './BaiboonAnswerView';
import BaiboonMediaPreview from './BaiboonMediaPreview';

const INPUT_H = 42;
const PAGE_SIZE = 30;
const MAX_MESSAGES = 120;
const LOAD_MORE_TOP_THRESHOLD = 72;

const GREETING = {
  id: 'greeting',
  role: 'bot',
  text: 'สวัสดีค่ะ น้องใบบุญเองค่ะ ผู้ช่วย HR ของ Boonsiri ยินดีช่วยเหลือตลอด 24 ชม. ถามได้เลยค่ะ',
};

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

function mapHistoryItem(m) {
  return {
    id: m.id,
    role: m.role,
    text: m.text,
    noPrompt: m.role === 'bot' && m.matched === false,
  };
}

const MessageBubble = React.memo(function MessageBubble({ msg, onMediaPreview }) {
  const isUser = msg.role === 'user';
  const isError = msg.isError;
  const isCard = !isUser && /bsr-wcard/.test(String(msg.text || ''));
  return (
    <Box
      sx={{
        alignSelf: isUser ? 'flex-end' : 'flex-start',
        maxWidth: isCard ? '100%' : '85%',
        px: isCard ? 0 : 1.5,
        py: isCard ? 0 : 1.25,
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        bgcolor: isUser ? 'primary.main' : isError ? alpha('#d32f2f', 0.08) : isCard ? 'transparent' : 'grey.100',
        color: isUser ? 'primary.contrastText' : isError ? 'error.dark' : 'text.primary',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        wordBreak: 'break-word',
      }}
    >
      {isUser || isError ? (
        <Typography component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {msg.text}
        </Typography>
      ) : (
        <BaiboonAnswerView text={msg.text} inverted={isUser} onMediaPreview={onMediaPreview} />
      )}
      {msg.noPrompt && (
        <Typography variant="caption" display="block" sx={{ mt: 0.75, color: 'warning.dark', fontWeight: 600 }}>
          ยังไม่มี prompt — บันทึกในรายการที่ยังไม่มีคำตอบแล้ว (Admin)
        </Typography>
      )}
    </Box>
  );
});

function TypingIndicator() {
  return (
    <Stack direction="row" spacing={0.5} sx={{ py: 1, px: 0.5, alignSelf: 'flex-start' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'text.disabled',
            animation: `${bounce} 1.2s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </Stack>
  );
}

function capMessages(list) {
  if (list.length <= MAX_MESSAGES) return list;
  return list.slice(-MAX_MESSAGES);
}

function friendlyError(err) {
  if (err?.status === 404) {
    return 'ระบบแชทยังไม่พร้อมบน server (404) — รอ deploy backend /api/baiboon ก่อนนะคะ';
  }
  if (err?.status === 429) {
    return 'ส่งข้อความบ่อยเกินไป รอสักครู่แล้วลองใหม่ค่ะ';
  }
  return err?.message || 'เชื่อมต่อไม่สำเร็จ ลองใหม่อีกครั้งค่ะ';
}

export default function BaiboonChatWidget() {
  const [cookies] = useCookies(['session']);
  const token = cookies.session;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chips, setChips] = useState(DEFAULT_BAIBOON_CHIPS);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);

  const bodyRef = useRef(null);
  const messagesRef = useRef(messages);
  const loadingMoreRef = useRef(false);
  const shortcutSettingsRef = useRef({
    chips: DEFAULT_BAIBOON_CHIPS,
    chipsUnmatched: DEFAULT_BAIBOON_CHIPS_UNMATCHED,
  });

  messagesRef.current = messages;

  const applyShortcutConfig = useCallback((data) => {
    const dropHr = (list) =>
      (list || []).filter((s) => !/คุยกับ\s*hr|เปิดแชทกับ\s*hr|ติดต่อ\s*hr/i.test(String(s || '')));
    const chips = dropHr(data?.chips?.length ? data.chips : DEFAULT_BAIBOON_CHIPS);
    const unmatched = dropHr(
      data?.chipsUnmatched?.length ? data.chipsUnmatched : DEFAULT_BAIBOON_CHIPS_UNMATCHED
    );
    shortcutSettingsRef.current = {
      chips: chips.length ? chips : dropHr(DEFAULT_BAIBOON_CHIPS),
      chipsUnmatched: unmatched.length ? unmatched : dropHr(DEFAULT_BAIBOON_CHIPS_UNMATCHED),
    };
  }, []);

  const chipsForMatch = useCallback((matched) => {
    const cfg = shortcutSettingsRef.current;
    return matched === false ? cfg.chipsUnmatched : cfg.chips;
  }, []);

  const handleMediaPreview = useCallback((item) => setMediaPreview(item), []);

  const hidden = useMemo(() => {
    const p = location.pathname;
    return !token || p === '/login' || p.startsWith('/loginCandidate') || p.startsWith('/registerCandidate');
  }, [token, location.pathname]);

  const scrollBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const loadOlder = useCallback(async () => {
    if (!token || loadingMoreRef.current || !hasMore) return;
    const oldest = messagesRef.current.find((m) => m.id && m.id !== 'greeting');
    if (!oldest?.id) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const res = await getChatHistory(token, { limit: PAGE_SIZE, before: oldest.id });
      const data = res?.data || res || {};
      const items = data.items || [];
      if (items.length) {
        setMessages((prev) => capMessages([...items.map(mapHistoryItem), ...prev]));
      }
      setHasMore(Boolean(data.hasMore));
    } catch {
      /* keep current list */
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [token, hasMore]);

  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el || !historyLoaded || loadingMoreRef.current || !hasMore) return;
    if (el.scrollTop < LOAD_MORE_TOP_THRESHOLD) loadOlder();
  }, [historyLoaded, hasMore, loadOlder]);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    setHistoryLoaded(false);
    setHasMore(false);

    (async () => {
      try {
        const [historyRes, settingsRes] = await Promise.all([
          getChatHistory(token, { limit: PAGE_SIZE }),
          getBaiboonSettings(token).catch(() => null),
        ]);
        if (cancelled) return;
        if (settingsRes?.data) applyShortcutConfig(settingsRes.data);

        const data = historyRes?.data || historyRes || {};
        const items = data.items || [];
        if (items.length) {
          setMessages(capMessages(items.map(mapHistoryItem)));
          setHasMore(Boolean(data.hasMore));
        } else {
          setMessages([GREETING]);
          setHasMore(false);
        }
        setChips(chipsForMatch(true));
      } catch {
        if (!cancelled) {
          setMessages([GREETING]);
          setHasMore(false);
          setChips(chipsForMatch(true));
        }
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, token, applyShortcutConfig, chipsForMatch]);

  useEffect(() => {
    if (historyLoaded) scrollBottom();
  }, [historyLoaded, scrollBottom]);

  useEffect(() => {
    if (!open) {
      setHistoryLoaded(false);
      setHasMore(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [open]);

  const pushMessage = (msg) => {
    setMessages((prev) => capMessages([...prev, msg]));
    scrollBottom();
  };

  const handleSend = async (text) => {
    const t = (text || input).trim();
    if (!t || sending || !token) return;
    setInput('');
    setChips([]);
    pushMessage({ role: 'user', text: t, id: `local-u-${Date.now()}` });
    setSending(true);
    try {
      const res = await sendChat(token, t);
      const data = res?.data || {};
      pushMessage({
        id: `local-b-${Date.now()}`,
        role: 'bot',
        text: data.reply || 'ขออภัยค่ะ ไม่สามารถตอบได้ในขณะนี้',
        noPrompt: data.matched === false && data.source !== 'shift',
      });
      if (Array.isArray(data.choices) && data.choices.length) {
        setChips(data.choices);
      } else {
        setChips(chipsForMatch(data.matched === false ? false : true));
      }
    } catch (err) {
      pushMessage({ role: 'bot', text: friendlyError(err), isError: true, id: `local-e-${Date.now()}` });
      setChips(chipsForMatch(true));
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      <Fab
        color="primary"
        aria-label="คุยกับน้องใบบุญ"
        onClick={() => setOpen((v) => !v)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #2b6fd6, #1a4a7a)',
          boxShadow: '0 8px 24px rgba(43,111,214,0.4)',
          '&:hover': { transform: 'scale(1.05)' },
        }}
      >
        <Iconify icon="fa-solid fa-comment-dots" width={26} color="#fff" />
      </Fab>

      {open && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 24,
            width: 380,
            maxWidth: 'calc(100vw - 40px)',
            height: 520,
            maxHeight: 'calc(100vh - 130px)',
            zIndex: 1300,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.5,
              minHeight: 56,
              background: 'linear-gradient(135deg, #2b6fd6, #1a4a7a)',
              color: '#fff',
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                น้องใบบุญ
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, lineHeight: 1.2 }}>
                ผู้ช่วย HR · 24 ชม.
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: '#fff', width: 36, height: 36 }}
            >
              <Iconify icon="fa-solid fa-xmark" width={18} />
            </IconButton>
          </Stack>

          <Box
            ref={bodyRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 2,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              bgcolor: '#f4f7fb',
            }}
          >
            {!historyLoaded ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </Stack>
            ) : (
              <>
                {(hasMore || loadingMore) && (
                  <Stack alignItems="center" sx={{ py: 0.5 }}>
                    {loadingMore ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Button size="small" variant="text" onClick={loadOlder}>
                        โหลดข้อความเก่า
                      </Button>
                    )}
                  </Stack>
                )}
                {messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} onMediaPreview={handleMediaPreview} />
                ))}
              </>
            )}
            {sending && <TypingIndicator />}
          </Box>

          <Box sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', bgcolor: '#fff' }}>
            {chips.length > 0 && (
              <Box
                sx={{
                  px: 1.5,
                  pt: 1.25,
                  pb: 0.75,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  justifyContent: 'flex-start',
                }}
              >
                {chips.map((c, i) => (
                  <Chip
                    key={`${c}-${i}`}
                    label={c}
                    size="small"
                    variant="outlined"
                    onClick={() => handleSend(c)}
                    sx={{
                      cursor: 'pointer',
                      height: 28,
                      fontSize: '0.75rem',
                      borderRadius: '14px',
                      '&:hover': { bgcolor: alpha('#1a4a7a', 0.06) },
                    }}
                  />
                ))}
              </Box>
            )}

            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="พิมพ์ข้อความ..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: INPUT_H,
                    borderRadius: 2,
                    bgcolor: '#f8fafc',
                  },
                  '& .MuiOutlinedInput-input': {
                    py: 0,
                    px: 1.5,
                    fontSize: '0.875rem',
                  },
                }}
              />
              <IconButton
                onClick={() => handleSend()}
                disabled={sending || !input.trim()}
                aria-label="ส่งข้อความ"
                sx={{
                  width: INPUT_H,
                  height: INPUT_H,
                  flexShrink: 0,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                {sending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Iconify icon="fa-solid fa-paper-plane" width={16} />
                )}
              </IconButton>
            </Stack>
          </Box>
        </Paper>
      )}

      <BaiboonMediaPreview
        open={Boolean(mediaPreview)}
        type={mediaPreview?.type}
        url={mediaPreview?.url}
        title={mediaPreview?.title}
        onClose={() => setMediaPreview(null)}
      />
    </>
  );
}
